import React, { useState, useEffect } from 'react';
import DataTable from '../components/common/DataTable';
import Modal from '../components/common/Modal';
import VentaForm from '../components/Forms/VentaForm';
import { ventasService } from '../services/ventasService';
import { formatCurrency, formatDate } from '../utils/formatters';
import '../styles/components.css';

const VentasPage = () => {
  
  const [ventas, setVentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVenta, setSelectedVenta] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('view');
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    loadVentas();
  }, []);

  const loadVentas = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ventasService.getVentas();
      setVentas(data.results || data);
    } catch (err) {
      setError(err.message || 'Error al cargar ventas');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (venta) => {
    setSelectedVenta(venta);
    setModalType('view');
    setShowModal(true);
  };

  const handleCreate = () => {
    setSelectedVenta(null);
    setModalType('create');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedVenta(null);
    setFormLoading(false);
  };

  const handleFormSubmit = async (formData) => {
    try {
      setFormLoading(true);
      
      await ventasService.createVenta(formData);
      alert('Venta registrada exitosamente. Actualiza el Dashboard para ver los cambios.');
      
      handleCloseModal();
      loadVentas();
    } catch (err) {
      alert(err.message || 'Error al registrar la venta');
      setFormLoading(false);
    }
  };

  const handleAnular = async (venta) => {
    const motivo = prompt('Ingrese el motivo de anulación:');
    if (!motivo) return;

    if (window.confirm(`¿Está seguro de anular la venta #${venta.id}?`)) {
      try {
        await ventasService.anularVenta(venta.id, motivo);
        alert('Venta anulada exitosamente');
        loadVentas();
      } catch (err) {
        alert(err.message || 'Error al anular la venta');
      }
    }
  };

  const columns = [
    {
      key: 'id',
      label: 'N° Venta',
      sortable: true
    },
    {
      key: 'fecha',
      label: 'Fecha',
      sortable: true,
      render: (value) => formatDate(value)
    },
    {
      key: 'cliente_nombre',
      label: 'Cliente',
      sortable: true,
      render: (value) => value || 'Cliente General'
    },
    {
      key: 'total',
      label: 'Total',
      sortable: true,
      render: (value) => formatCurrency(value)
    },
    {
      key: 'metodo_pago',
      label: 'Método Pago',
      sortable: true,
      render: (value) => {
        const metodos = {
          'efectivo': 'Efectivo',
          'tarjeta': 'Tarjeta',
          'transferencia': 'Transferencia'
        };
        return metodos[value] || value;
      }
    },
    {
      key: 'estado',
      label: 'Estado',
      sortable: true,
      render: (value) => (
        <span className={`badge badge-${value === 'completada' ? 'success' : value === 'anulada' ? 'danger' : 'warning'}`}>
          {value === 'completada' ? 'Completada' : value === 'anulada' ? 'Anulada' : 'Pendiente'}
        </span>
      )
    }
  ];

  const getActions = (venta) => {
    const actions = [
      {
        label: 'Ver',
        onClick: () => handleView(venta),
        className: 'btn-info'
      }
    ];

    if (venta.estado === 'completada') {
      actions.push({
        label: 'Anular',
        onClick: () => handleAnular(venta),
        className: 'btn-danger'
      });
    }

    return actions;
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">💰 Gestión de Ventas</h1>
        <button className="btn btn-primary" onClick={handleCreate}>
          ➕ Nueva Venta
        </button>
      </div>

      <div className="page-content">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Listado de Ventas</h2>
          </div>
          <div className="card-body">
            <DataTable
              columns={columns}
              data={ventas}
              loading={loading}
              error={error}
              actions={getActions}
              emptyMessage="No hay ventas registradas"
            />
          </div>
        </div>
      </div>

      {/* Modal para ver/crear venta */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={
          modalType === 'view' ? 'Detalles de la Venta' :
          'Nueva Venta'
        }
        size="large"
      >
        {modalType === 'view' && selectedVenta && (
          <div className="venta-details">
            <div className="detail-row">
              <span className="detail-label">N° Venta:</span>
              <span className="detail-value">{selectedVenta.id}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Fecha:</span>
              <span className="detail-value">{formatDate(selectedVenta.fecha)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Cliente:</span>
              <span className="detail-value">{selectedVenta.cliente_nombre || 'Cliente General'}</span>
            </div>
            {selectedVenta.cliente_rut && (
              <div className="detail-row">
                <span className="detail-label">RUT:</span>
                <span className="detail-value">{selectedVenta.cliente_rut}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-label">Método de Pago:</span>
              <span className="detail-value">
                {selectedVenta.metodo_pago === 'efectivo' ? 'Efectivo' :
                 selectedVenta.metodo_pago === 'tarjeta' ? 'Tarjeta' :
                 selectedVenta.metodo_pago === 'transferencia' ? 'Transferencia' :
                 selectedVenta.metodo_pago}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Subtotal:</span>
              <span className="detail-value">{formatCurrency(selectedVenta.subtotal)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Descuento:</span>
              <span className="detail-value">{formatCurrency(selectedVenta.descuento || 0)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Total:</span>
              <span className="detail-value" style={{ fontSize: '1.2em', fontWeight: 'bold' }}>
                {formatCurrency(selectedVenta.total)}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Estado:</span>
              <span className="detail-value">
                <span className={`badge badge-${selectedVenta.estado === 'completada' ? 'success' : selectedVenta.estado === 'anulada' ? 'danger' : 'warning'}`}>
                  {selectedVenta.estado === 'completada' ? 'Completada' : selectedVenta.estado === 'anulada' ? 'Anulada' : 'Pendiente'}
                </span>
              </span>
            </div>
            {selectedVenta.observaciones && (
              <div className="detail-row">
                <span className="detail-label">Observaciones:</span>
                <span className="detail-value">{selectedVenta.observaciones}</span>
              </div>
            )}
          </div>
        )}
        
        {modalType === 'create' && (
          <VentaForm
            onSubmit={handleFormSubmit}
            onCancel={handleCloseModal}
            loading={formLoading}
          />
        )}
      </Modal>
    </div>
  );
};

export default VentasPage;

// Made with Bob
